import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { getOperationalOrdersQueryKey } from "./use-get-operational-orders";
import { regroupOrders } from "./use-subscribe-to-venue-orders";
import { OrderFragment } from "./fragments/order";
import { getPrepStationsOrdersQueryKey } from "~/queries/use-get-prep-stations-orders";
import { gqlReq } from "~/libs/gql";
import {
	GetOperationalOrdersQuery,
	GetOrderByIdDocument,
} from "~/types/__generated/gql/graphql";
import { queryClient } from "~/providers/query-provider";

void (
	/* GraphQL */ `
		query GetOrderById($id: ObjectID!) {
			getOrderById(id: $id) {
				...OrderFragment
			}
		}
		${OrderFragment}
	`
);

export const getOrderByIdQueryKey = (id: string) => ["getOrderById", id];

export const getOrderByIdQueryFn = async (id: string, signal?: AbortSignal) => {
	const variables = { id };
	return gqlReq({
		document: GetOrderByIdDocument,
		variables,
		signal,
	});
};

export const useGetOrderById = (id?: string) => {
	const queryClient = useQueryClient();

	return useQuery({
		enabled: !!id,
		queryKey: getOrderByIdQueryKey(id!),
		queryFn: () => getOrderByIdQueryFn(id!), // TOOD: find out why passing signal causes query to cancel with updateOrderStatus/cancelOrder mutations
		onSuccess: (data) => {
			queryClient.setQueryData<GqlResultOf<GetOperationalOrdersQuery>>(
				getOperationalOrdersQueryKey(),
				// @ts-expect-error
				(oldData) => {
					if (oldData) return regroupOrders(data, oldData);
				},
			);
			// This query does not select KDS hold fields; never overwrite that richer cache.
			void queryClient.invalidateQueries({
				queryKey: getPrepStationsOrdersQueryKey(),
			});
		},
	});
};

export const useGetCurrentOrder = () => {
	const {
		query: { id },
	} = useRouter();

	return useGetOrderById(id?.toString());
};

export const getOrderByIdCache = (id: string) => {
	return queryClient.ensureQueryData({
		queryKey: getOrderByIdQueryKey(id),
		queryFn: () => getOrderByIdQueryFn(id),
	});
};
